import React, { useState, useRef } from 'react';
import { curveMonotoneX } from '@visx/curve';
import { AreaChart, Area } from '../../components/charts/area-chart';
import { BarChart } from '../../components/charts/bar-chart';
import { Bar } from '../../components/charts/bar';
import { Grid } from '../../components/charts/grid';
import { XAxis } from '../../components/charts/x-axis';
import { BarXAxis } from '../../components/charts/bar-x-axis';
import { YAxis } from '../../components/charts/y-axis';
import { ChartTooltip } from '../../components/charts/tooltip';
import './MobileSwipeableCharts.css';

// Native chart language (RN TrendChart parity): dashed hairline grid, muted
// ticks, accent series with a soft gradient area fill. Grid/axis/tooltip
// chrome resolves from the --chart-* tokens the bklit kit reads.
const ACCENT = '#ee7623';
const CHART_MARGIN = { top: 8, right: 8, bottom: 28, left: 44 };

// bklit time-series shells coerce x values through new Date(); dashboard rows
// carry display strings ("Aug 5"), so synthesize evenly spaced dates for
// spacing and surface the real labels via xLabels + tooltip title.
const withSyntheticDates = (data) =>
  data.map((d, i) => ({ ...d, chartX: new Date(Date.UTC(2024, 0, 1 + i)) }));

export const MobileSwipeableCharts = ({ charts, formatCurrency, formatPercentage }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollRef = useRef(null);

  const handleScroll = (e) => {
    if (scrollRef.current) {
      const scrollLeft = e.target.scrollLeft;
      const cardWidth = e.target.offsetWidth;
      const index = Math.round(scrollLeft / cardWidth);
      setCurrentIndex(index);
    }
  };

  const formatYAxis = (value, type) => {
    if (type === 'currency') {
      if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
      if (value >= 1000) return `$${Math.round(value / 1000)}k`;
      return `$${Math.round(value)}`;
    }
    if (type === 'percentage') {
      return `${Math.round(value)}%`;
    }
    return Math.round(value).toString();
  };

  const formatTooltipValue = (value, type) => {
    if (type === 'currency') return formatCurrency(value);
    if (type === 'percentage') return formatPercentage(value);
    return value;
  };

  return (
    <div className="mobile-swipeable-charts">
      <div
        className="mobile-charts-container"
        ref={scrollRef}
        onScroll={handleScroll}
      >
        {charts.map((chart, index) => (
          <div key={index} className="mobile-chart-card">
            <div className="mobile-chart-header">
              <h3>{chart.title}</h3>
            </div>
            <div className="mobile-chart-content">
              {chart.type === 'line' && (
                <AreaChart
                  data={withSyntheticDates(chart.data)}
                  xDataKey="chartX"
                  xLabels={chart.data.map((d) => d.date)}
                  aspectRatio={null}
                  style={{ height: 200 }}
                  margin={CHART_MARGIN}
                >
                  <Grid horizontal />
                  <XAxis />
                  <YAxis formatValue={(value) => formatYAxis(value, chart.yAxisType)} />
                  <Area
                    dataKey="totalRevenue"
                    fill={ACCENT}
                    strokeWidth={2.5}
                    curve={curveMonotoneX}
                    fillOpacity={0.18}
                    gradientToOpacity={0.01}
                  />
                  <ChartTooltip
                    title={null}
                    rows={(point) => [
                      { color: ACCENT, label: 'Revenue', value: formatTooltipValue(point.totalRevenue, chart.yAxisType) },
                    ]}
                  />
                </AreaChart>
              )}
              {chart.type === 'bar' && (
                <BarChart
                  data={chart.data}
                  xDataKey="date"
                  aspectRatio={null}
                  style={{ height: 200 }}
                  margin={CHART_MARGIN}
                  barGap={0.24}
                >
                  <Grid horizontal />
                  <BarXAxis />
                  <YAxis formatValue={(value) => formatYAxis(value, chart.yAxisType)} />
                  <Bar dataKey="occupancyRate" fill={ACCENT} lineCap={3} />
                  <ChartTooltip
                    title={null}
                    rows={(point) => [
                      { color: ACCENT, label: 'Occupancy', value: formatTooltipValue(point.occupancyRate, chart.yAxisType) },
                    ]}
                  />
                </BarChart>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Page Indicators */}
      {charts.length > 1 && (
        <div className="mobile-chart-indicators">
          {charts.map((_, index) => (
            <div
              key={index}
              className={`mobile-chart-indicator ${index === currentIndex ? 'active' : ''}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};
